import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Text, Loader, createStyles } from "@mantine/core";
import {
  IconChevronLeft,
  IconSettings,
  IconSparkles,
  IconSignature,
  IconFileDescription,
} from "@tabler/icons-react";
import useSharedStyles from "../sharedStyles";
import { QR, BTN_RESET } from "../theme";
import PurpleSwitch from "../components/PurpleSwitch";
import PurpleSegmented from "../components/PurpleSegmented";

const useStyles = createStyles(() => ({
  layout: { display: "flex", gap: 16, alignItems: "flex-start" },
  rail: {
    width: 230,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
    padding: 8,
    position: "sticky",
    top: 91,
  },
  railItem: {
    ...BTN_RESET,
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 9,
    padding: "9px 11px",
    borderRadius: 8,
    fontSize: 13.5,
    fontWeight: 600,
    color: QR.ink2,
    fontFamily: "Inter",
    "&:hover": { background: QR.surfaceSoft },
  },
  railItemActive: {
    background: QR.purple,
    color: "#fff",
    "&:hover": { background: QR.purple },
  },
  panel: {
    flex: 1,
    background: QR.surface,
    border: `1px solid ${QR.line}`,
    borderRadius: 12,
    boxShadow: QR.shadowCard,
  },
  panelHead: { padding: "16px 20px", borderBottom: `1px solid ${QR.lineSoft}` },
  panelTitle: { fontSize: 15, fontWeight: 700, fontFamily: "Inter" },
  // .card-body's generic 20px padding — .set-row itself only carries its own
  // vertical 14px 0 and a top (not bottom) divider.
  panelBody: { padding: 20 },
  row: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    padding: "14px 0",
    borderTop: `1px solid ${QR.lineSoft}`,
    "&:first-of-type": { borderTop: "none" },
  },
  rowColumn: { flexDirection: "column", alignItems: "flex-start", gap: 10 },
  rowMain: { maxWidth: "60ch" },
  rowTitle: { fontSize: 14, fontWeight: 600, fontFamily: "Inter", color: QR.ink },
  rowSub: { fontSize: 12.5, color: QR.muted, marginTop: 2, lineHeight: 1.5 },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: {
    ...BTN_RESET,
    border: `1px solid ${QR.line}`,
    background: QR.surfaceAlt,
    borderRadius: 6,
    padding: "7px 12px",
    fontSize: 13,
    fontWeight: 600,
    color: QR.ink2,
    fontFamily: "Inter",
  },
  chipOn: { background: QR.accentSoft, color: "#92400E", borderColor: QR.accentRing },
  input: {
    height: 38,
    border: `1px solid ${QR.line}`,
    borderRadius: 8,
    padding: "0 12px",
    fontSize: 13.5,
    color: QR.ink,
    background: QR.surface,
    fontFamily: "Inter",
    width: "100%",
  },
}));

const PANELS = [
  { key: "general", label: "General", icon: IconSettings },
  { key: "tara", label: "Tara Ai Drafting", icon: IconSparkles },
  { key: "manual", label: "Manual Reports", icon: IconFileDescription },
  { key: "finalize", label: "Sign Document", icon: IconSignature },
];

// Evidence formats a manual report accepts, stored as bare extensions so the
// server can validate an upload by filename.
const EVIDENCE_TYPES = [
  { label: "Images", keys: ["jpg", "jpeg", "png", "heic", "heif"] },
  { label: "Word", keys: ["doc", "docx"] },
  { label: "PDF", keys: ["pdf"] },
  { label: "Excel", keys: ["xls", "xlsx"] },
];

// Every other control on this screen saves on the spot (a single click or
// toggle is already a deliberate, discrete change), but a free-text setting is
// typed character by character — without debouncing, every keystroke fires its
// own PUT /settings, and out-of-order responses could land back in Redux after
// a later keystroke's, snapping the saved value back to a stale partial string
// (which is what the report title / PDF filename is then built from). Local
// state drives the input directly and updates synchronously on every keystroke
// — typing is never throttled; only the network save is, via a plain timer so
// there's no extra library-hook overhead between a keystroke and the input
// repainting.
//
// The sync from the saved value is deliberately one-shot (guarded by
// initializedRef), not a live subscription — settings arrives async on mount
// (starts as {} until getReportSettings() resolves), so this still needs to
// pick up that first real value. But our own debounced save completing ALSO
// changes it, and if that response lands while the user has kept typing past
// the 500ms mark, a live sync would snap the input back to the just-saved (now
// stale) value mid-keystroke. Once initialized, local state is the sole source
// of truth for this field for the rest of the component's lifetime.
const DebouncedSettingInput = ({ value, onCommit, className }) => {
  const [text, setText] = useState(value);
  const initializedRef = useRef(value != null);
  useEffect(() => {
    if (initializedRef.current || value == null) return;
    setText(value);
    initializedRef.current = true;
  }, [value]);

  // onCommit closes over the latest settings object; held in a ref so a
  // pending timer fires with the current one rather than the one captured
  // when the keystroke happened.
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleChange = (e) => {
    const next = e.target.value;
    setText(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commitRef.current(next), 500);
  };

  return <input className={className} value={text ?? ""} onChange={handleChange} />;
};

const SettingsView = ({ onBack, settings, onChange, saving }) => {
  const { classes: shared } = useSharedStyles();
  const { classes, cx } = useStyles();
  const [active, setActive] = useState("general");

  // Each chip stands for a family of extensions (Word = doc + docx), so it is
  // "on" when any of its extensions is allowed and toggles all of them at once.
  const toggleEvidenceType = (keys) => {
    const current = settings.manualEvidenceTypes || [];
    const on = keys.some((k) => current.includes(k));
    const next = on ? current.filter((k) => !keys.includes(k)) : [...current, ...keys];
    onChange({ ...settings, manualEvidenceTypes: next });
  };

  const toggleSigner = (key) => {
    const next = settings.whoCanSign.includes(key)
      ? settings.whoCanSign.filter((k) => k !== key)
      : [...settings.whoCanSign, key];
    onChange({ ...settings, whoCanSign: next });
  };

  return (
    <Box className={shared.content}>
      <Flex className={shared.crumbBar}>
        <button className={shared.backBtn} onClick={onBack}>
          <IconChevronLeft size={16} /> Back
        </button>
      </Flex>

      <Flex align="center" gap={10}>
        <Text className={shared.wlTitle}>Report Settings</Text>
        {saving && (
          <Flex align="center" gap={6}>
            <Loader size={13} color="violet" />
            <Text size="xs" color="dimmed">
              Saving…
            </Text>
          </Flex>
        )}
      </Flex>

      <Flex className={classes.layout}>
        <Box className={classes.rail}>
          {PANELS.map((p) => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                className={cx(classes.railItem, active === p.key && classes.railItemActive)}
                onClick={() => setActive(p.key)}
              >
                <Icon size={16} /> {p.label}
              </button>
            );
          })}
        </Box>

        {active === "general" && (
          <Box className={classes.panel}>
            <Box className={classes.panelHead}>
              <Text className={classes.panelTitle}>General</Text>
            </Box>
            <Box className={classes.panelBody}>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Quarter basis</Text>
                  <Text className={classes.rowSub}>How the quarter presets in the generate flow are calculated.</Text>
                </Box>
                <PurpleSegmented
                  value={settings.quarterBasis}
                  onChange={(v) => onChange({ ...settings, quarterBasis: v })}
                  data={[
                    { label: "Calendar year", value: "calendar" },
                    { label: "ISP plan year", value: "isp" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Block duplicate reports</Text>
                  <Text className={classes.rowSub}>
                    Prevent generating a second report for the same client and review period. You can still
                    regenerate from the report itself.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.blockDuplicates}
                  onChange={(e) => onChange({ ...settings, blockDuplicates: e.currentTarget.checked })}
                />
              </Flex>
              {/* Report title format has no second column — the input sits below
                  the label/hint in the same block, matching .set-row-main's
                  own flex:1 column in the prototype. */}
              <Box className={cx(classes.row, classes.rowColumn)}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Report title format</Text>
                  <Text className={classes.rowSub} mb={8}>
                    Used for the report list and PDF cover.
                  </Text>
                  <DebouncedSettingInput
                    className={classes.input}
                    value={settings.titleFormat}
                    onCommit={(value) => onChange({ ...settings, titleFormat: value })}
                  />
                </Box>
              </Box>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Default review period</Text>
                  <Text className={classes.rowSub}>
                    Which quarter is preselected when you start a report.
                  </Text>
                </Box>
                <PurpleSegmented
                  value={settings.defaultPeriod}
                  onChange={(v) => onChange({ ...settings, defaultPeriod: v })}
                  data={[
                    { label: "Last quarter", value: "last" },
                    { label: "Current quarter", value: "current" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Past due reminder</Text>
                  <Text className={classes.rowSub}>
                    Flag a report as past due this many days after the quarter ends.
                  </Text>
                </Box>
                {/* Stored as a number, so the segmented control's string values
                    are converted on the way in and out. */}
                <PurpleSegmented
                  value={String(settings.pastDueDays ?? 15)}
                  onChange={(v) => onChange({ ...settings, pastDueDays: Number(v) })}
                  data={[
                    { label: "10 days", value: "10" },
                    { label: "15 days", value: "15" },
                    { label: "30 days", value: "30" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Include provider logo on PDF</Text>
                  <Text className={classes.rowSub}>
                    Shown in the header of the exported document.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.includeLogo}
                  onChange={(e) => onChange({ ...settings, includeLogo: e.currentTarget.checked })}
                />
              </Flex>
              <Box className={cx(classes.row, classes.rowColumn)}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>PDF filename format</Text>
                  <Text className={classes.rowSub} mb={8}>
                    Placeholders: {"{client}"}, {"{quarter}"}, {"{provider}"}.
                  </Text>
                  <DebouncedSettingInput
                    className={classes.input}
                    value={settings.filenameFormat}
                    onCommit={(value) => onChange({ ...settings, filenameFormat: value })}
                  />
                </Box>
              </Box>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Notify me when a batch finishes</Text>
                  <Text className={classes.rowSub}>
                    In-app notification when all reports in a generation run are done.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.notifyOnBatch}
                  onChange={(e) => onChange({ ...settings, notifyOnBatch: e.currentTarget.checked })}
                />
              </Flex>
            </Box>
          </Box>
        )}

        {active === "tara" && (
          <Box className={classes.panel}>
            <Box className={classes.panelHead}>
              <Text className={classes.panelTitle}>Tara Ai Drafting</Text>
            </Box>
            <Box className={classes.panelBody}>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Draft only from approved notes</Text>
                  <Text className={classes.rowSub}>
                    Tara never uses pending or rejected documentation. This is required for compliance and
                    cannot be turned off.
                  </Text>
                </Box>
                <PurpleSwitch checked disabled />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Cite note dates in narratives</Text>
                  <Text className={classes.rowSub}>
                    Each drafted statement references the date it was documented, e.g. "On 04/12/2026 staff
                    documented…".
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.citeNoteDates}
                  onChange={(e) => onChange({ ...settings, citeNoteDates: e.currentTarget.checked })}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Clients without an active ISP</Text>
                  <Text className={classes.rowSub}>What Tara does when no plan overlaps the review period.</Text>
                </Box>
                <PurpleSegmented
                  value={settings.noIspBehavior}
                  onChange={(v) => onChange({ ...settings, noIspBehavior: v })}
                  data={[
                    { label: "Generate review", value: "generate" },
                    { label: "Skip client", value: "skip" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Clients with no approved notes</Text>
                  <Text className={classes.rowSub}>What Tara does when there is nothing documented for the period.</Text>
                </Box>
                <PurpleSegmented
                  value={settings.noNotesBehavior}
                  onChange={(v) => onChange({ ...settings, noNotesBehavior: v })}
                  data={[
                    { label: "Generate review", value: "generate" },
                    { label: "Skip client", value: "skip" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Narrative length</Text>
                  <Text className={classes.rowSub}>How much detail Tara writes per outcome and question.</Text>
                </Box>
                <PurpleSegmented
                  value={settings.narrativeLength}
                  onChange={(v) => onChange({ ...settings, narrativeLength: v })}
                  data={[
                    { label: "Concise", value: "concise" },
                    { label: "Standard", value: "standard" },
                    { label: "Detailed", value: "detailed" },
                  ]}
                />
              </Flex>
            </Box>
          </Box>
        )}

        {active === "manual" && (
          <Box className={classes.panel}>
            <Box className={classes.panelHead}>
              <Text className={classes.panelTitle}>Manual Reports</Text>
            </Box>
            <Box className={classes.panelBody}>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Allow manual reports</Text>
                  <Text className={classes.rowSub}>
                    Create a DBHDS review by hand for providers who document in their own EHR. No AI
                    drafting.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.allowManualReports !== false}
                  onChange={(e) => onChange({ ...settings, allowManualReports: e.currentTarget.checked })}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Available for</Text>
                  <Text className={classes.rowSub}>Which provider types can have a manual report.</Text>
                </Box>
                <PurpleSegmented
                  value={settings.manualAvailableFor || "external"}
                  onChange={(v) => onChange({ ...settings, manualAvailableFor: v })}
                  data={[
                    { label: "Ogiso only", value: "ogiso" },
                    { label: "External only", value: "external" },
                    { label: "All providers", value: "all" },
                  ]}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Require evidence before finalizing</Text>
                  <Text className={classes.rowSub}>
                    At least one uploaded file must be attached to the report.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={!!settings.manualRequireEvidence}
                  onChange={(e) =>
                    onChange({ ...settings, manualRequireEvidence: e.currentTarget.checked })
                  }
                />
              </Flex>
              <Box className={cx(classes.row, classes.rowColumn)}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Allowed evidence types</Text>
                  <Text className={classes.rowSub} mb={8}>
                    File formats that can be attached as reference material.
                  </Text>
                  <Flex className={classes.chipRow}>
                    {EVIDENCE_TYPES.map((t) => (
                      <button
                        key={t.label}
                        className={cx(
                          classes.chip,
                          t.keys.some((k) => (settings.manualEvidenceTypes || []).includes(k)) &&
                            classes.chipOn
                        )}
                        onClick={() => toggleEvidenceType(t.keys)}
                      >
                        {t.label}
                      </button>
                    ))}
                  </Flex>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {active === "finalize" && (
          <Box className={classes.panel}>
            <Box className={classes.panelHead}>
              <Text className={classes.panelTitle}>Sign Document</Text>
            </Box>
            <Box className={classes.panelBody}>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Require reviewer title</Text>
                  <Text className={classes.rowSub}>
                    The completed-by title (e.g. QIDP) must be entered before a report can be finalized.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.requireTitle}
                  onChange={(e) => onChange({ ...settings, requireTitle: e.currentTarget.checked })}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Lock reports after finalizing</Text>
                  <Text className={classes.rowSub}>
                    Finalized reports become read-only. Unlocking requires deleting and regenerating.
                  </Text>
                </Box>
                <PurpleSwitch
                  checked={settings.lockAfterFinalize}
                  onChange={(e) => onChange({ ...settings, lockAfterFinalize: e.currentTarget.checked })}
                />
              </Flex>
              <Flex className={classes.row}>
                <Box className={classes.rowMain}>
                  <Text className={classes.rowTitle}>Who can sign</Text>
                  <Text className={classes.rowSub}>Staff members who may sign and lock quarterly reviews.</Text>
                </Box>
                <Flex className={classes.chipRow}>
                  {[
                    { key: "admin", label: "Account Admin" },
                    { key: "adminStaff", label: "Admin staff" },
                    { key: "allStaff", label: "All staff" },
                  ].map((c) => (
                    <button
                      key={c.key}
                      className={cx(classes.chip, settings.whoCanSign.includes(c.key) && classes.chipOn)}
                      onClick={() => toggleSigner(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </Flex>
              </Flex>
            </Box>
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export const DEFAULT_REPORT_SETTINGS = {
  quarterBasis: "calendar",
  allowManualReports: true,
  manualAvailableFor: "external",
  manualRequireEvidence: false,
  manualEvidenceTypes: ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "heic", "heif"],
  blockDuplicates: true,
  titleFormat: "VA Quarterly Review · {client}",
  citeNoteDates: true,
  noIspBehavior: "generate",
  noNotesBehavior: "generate",
  narrativeLength: "standard",
  requireTitle: true,
  lockAfterFinalize: true,
  whoCanSign: ["admin", "adminStaff"],
};

export default SettingsView;
